<template>
  <div class="run-list">
    <BaseInputSelect
      :value="activeRunId || undefined"
      :options="runOptions"
      select-only
      placeholder="No runs yet"
      class="run-select"
      @selected="onSelected($event.value)"
    />
    <BaseButton
      kind="auxiliary"
      :state="canGoPrev ? 'default' : 'disabled'"
      @click="goPrev"
    >
      <template #icon><ChevronLeftIcon /></template>
    </BaseButton>
    <BaseButton
      kind="auxiliary"
      :state="canGoNext ? 'default' : 'disabled'"
      @click="goNext"
    >
      <template #icon><ChevronRightIcon /></template>
    </BaseButton>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onBeforeUnmount, type PropType } from "vue";

import type {
  AutomationId,
  AutomationRunId,
  OrgId,
} from "@ctrl/controller-rpc";

import { useAutomationRuns } from "@/components/ai/useAutomationRuns";
import BaseButton from "@/components/base/BaseButton.vue";
import BaseInputSelect, {
  type SelectOption,
} from "@/components/base/BaseInputSelect.vue";
import ChevronLeftIcon from "@/components/icons/ChevronLeftIcon.vue";
import ChevronRightIcon from "@/components/icons/ChevronRightIcon.vue";
import * as inject from "@/components/inject";

export default defineComponent({
  name: "AutomationRunList",
  components: {
    BaseButton,
    BaseInputSelect,
    ChevronLeftIcon,
    ChevronRightIcon,
  },
  props: {
    organizationId: { type: String as PropType<OrgId>, required: true },
    automationId: { type: String as PropType<AutomationId>, required: true },
    activeRunId: { type: String as PropType<AutomationRunId>, default: "" },
  },
  emits: {
    select: (_runId: AutomationRunId) => true,
  },
  async setup(props, { emit }) {
    const controller = inject.controller();

    const { init, sortedRuns, release } = useAutomationRuns(
      controller,
      props.organizationId,
      props.automationId
    );
    onBeforeUnmount(release);

    await init();

    const formatRunLabel = (run: { started_at: Date; status: string }) => {
      const date = run.started_at.toLocaleString(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
      });
      const statusMap: Record<string, string> = {
        completed: "Completed",
        failed: "Failed",
        pending_user_input: "Waiting",
        pending_resume: "Resuming",
      };
      const status = statusMap[run.status] ?? "Failed";
      return `${date} — ${status}`;
    };

    const runOptions = computed<SelectOption[]>(() =>
      sortedRuns.value.map((run) => ({
        value: run.id,
        label: formatRunLabel(run),
        aliases: [],
      }))
    );

    const currentIndex = computed(() =>
      sortedRuns.value.findIndex((r) => r.id === props.activeRunId)
    );

    // sortedRuns is newest-first: lower index = newer, higher index = older
    const canGoPrev = computed(() => currentIndex.value > 0);
    const canGoNext = computed(
      () =>
        currentIndex.value !== -1 &&
        currentIndex.value < sortedRuns.value.length - 1
    );

    const navigate = (runId: AutomationRunId) => {
      emit("select", runId);
    };

    const onSelected = (runId: string) => navigate(runId);

    const goNext = () => {
      const run = sortedRuns.value[currentIndex.value + 1];
      if (run) navigate(run.id);
    };

    const goPrev = () => {
      const run = sortedRuns.value[currentIndex.value - 1];
      if (run) navigate(run.id);
    };

    if (sortedRuns.value.length > 0 && !props.activeRunId) {
      emit("select", sortedRuns.value[0].id);
    }

    return {
      runOptions,
      canGoPrev,
      canGoNext,
      onSelected,
      goPrev,
      goNext,
    };
  },
});
</script>

<style scoped lang="scss">
.run-list {
  display: flex;
  align-items: center;
  gap: 4px;
  width: 100%;

  .run-select {
    flex: 1;
    min-width: 0;
  }
}
</style>
